import React, { ChangeEvent } from 'react';
import {
  Button,
  IconButton,
  Icons,
  Popover,
  Text,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  styled,
} from '@/theme/index';
import { Nullable } from '@/utils/helperTypes';
import { useScreenSize } from '@/utils/useScreenSize';

export type ListingPropertyTypeFilterProps = {
  currentSearchType: string;
  setSearchParams: (
    arg: (searchParams: URLSearchParams) => URLSearchParams
  ) => void;
} & Nullable<{
  currentPropertyType: string;
}>;

export type PropertyTypeOption = {
  display: string;
  value: string;
  defaultChecked: boolean;
};

const ModalButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minWidth: '170px',
  maxWidth: '210px',
  height: '40px',
  marginRight: theme.spacing(2),
  padding: theme.spacing(0.5),
  justifyContent: 'space-between',

  p: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    maxWidth: '200px',
  },
}));

const ClearButton = styled(Button)(({ theme }) => ({
  minWidth: 'initial',
  padding: theme.spacing(0),
  textDecoration: 'underline',
}));

const StyledPopover = styled(Popover)(() => ({
  '.MuiPopover-paper': {
    width: '320px',
    maxWidth: 'initial',
    minHeight: '180px',
    borderRadius: '8px',
  },
}));

const TitleText = styled(Text)(({ theme }) => ({
  padding: theme.spacing(3.75, 2.5, 0.5),
  height: '88px',
  backgroundColor: theme.palette.secondary.main,
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  padding: theme.spacing(2.625),

  '.select-all': {
    margin: theme.spacing(-7.75, 0, 2.5),
    fontWeight: 'bold',
    textDecoration: 'underline',
  },
}));

export const propertyTypeDropdownText = (
  currentPropertyType: string | null,
  propertyTypeOptions: PropertyTypeOption[]
) => {
  const propertyType = currentPropertyType;

  if (propertyType?.length && propertyTypeOptions) {
    const propertyTypeValue = propertyType;
    const propertyTypeOptionObj = propertyTypeOptions.filter(
      (pto: PropertyTypeOption) => pto.value === propertyTypeValue
    );
    const displayValue = propertyTypeOptionObj.length
      ? propertyTypeOptionObj[0].display
      : propertyTypeValue;

    return `Property Type: ${displayValue}`;
  }

  return 'Property Type';
};

export const propertyTypeOptions = (
  currentPropertyType: string | null,
  currentSearchType: string
) => {
  const selectedPropertyType = currentPropertyType || '';
  const optionsPerSearchTypeMap = {
    for_sale: [
      {
        display: 'Select All',
        value: 'select-all',
        defaultChecked:
          selectedPropertyType === 'select-all' || !selectedPropertyType,
      },
      {
        display: 'Single Family',
        value: 'Residential',
        defaultChecked: selectedPropertyType === 'Residential',
      },
      {
        display: 'Multi-Family',
        value: 'Residential Multi Family',
        defaultChecked: selectedPropertyType === 'Residential Multi Family',
      },
      {
        display: 'Farm',
        value: 'Farm',
        defaultChecked: selectedPropertyType === 'Farm',
      },
      {
        display: 'Land',
        value: 'Land',
        defaultChecked: selectedPropertyType === 'Land',
      },
      {
        display: 'Other',
        value: 'Other',
        defaultChecked: selectedPropertyType === 'Other',
      },
    ],
  };

  return (
    optionsPerSearchTypeMap[
      currentSearchType as keyof typeof optionsPerSearchTypeMap
    ] || []
  );
};

/**
 * This component is responsible for rendering the property type filter on the Listings search page.
 * It renders a button and popover in desktop view or radio group in mobile view.
 */
export function ListingPropertyTypeFilter({
  currentSearchType,
  currentPropertyType,
  setSearchParams,
}: ListingPropertyTypeFilterProps) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const { isDesktop } = useScreenSize();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setSearchParams((searchParams) => {
      if (event.target.value === 'select-all') {
        searchParams.delete('type');
      } else {
        searchParams.set('type', event.target.value);
      }
      return searchParams;
    });
  };

  const handleClear = () => {
    setSearchParams((searchParams) => {
      searchParams.delete('type');
      setAnchorEl(null);

      return searchParams;
    });
  };

  const buttonIcon = () => {
    if (modalButtonText === 'Property Type') {
      if (open) {
        return (
          <Icons.KeyboardArrowDown
            sx={{
              transition: 'all 0.2s ease-in-out',
              transform: 'rotate(-180deg)',
            }}
          />
        );
      }
      return (
        <Icons.KeyboardArrowDown
          sx={{
            transition: 'all 0.2s ease-in-out',
          }}
        />
      );
    }
    return (
      <IconButton>
        <Icons.Cancel
          onClick={handleClear}
          sx={{ width: '18px', height: '24px' }}
        />
      </IconButton>
    );
  };

  const open = Boolean(anchorEl);
  const modalButtonText = 'Property Type';
  const options = propertyTypeOptions(currentPropertyType, currentSearchType);

  return (
    <>
      {isDesktop && (
        <ModalButton color="primary" variant="outlined" onClick={handleClick}>
          <Text>{propertyTypeDropdownText(currentPropertyType, options)}</Text>
          {buttonIcon()}
        </ModalButton>
      )}
      <StyledPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack>
          <Stack
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <TitleText>Property Type</TitleText>
            <StyledRadioGroup onChange={handleChange} sx={{ margin: '0' }}>
              {propertyTypeOptions(currentPropertyType, currentSearchType).map(
                (propertyTypeOption, index: number) => (
                  <FormControlLabel
                    className={propertyTypeOption.value}
                    key={index}
                    sx={{ margin: '0' }}
                    control={
                      <Radio
                        sx={(theme) => ({ padding: theme.spacing(0.5, 0) })}
                        checked={propertyTypeOption.defaultChecked}
                        value={propertyTypeOption.value}
                      />
                    }
                    label={propertyTypeOption.display}
                  />
                )
              )}
            </StyledRadioGroup>
          </Stack>
          <Stack
            justifyContent="space-between"
            direction="row"
            sx={(theme) => ({ padding: theme.spacing(1, 2.625, 2) })}
          >
            <ClearButton onClick={handleClear}>Clear</ClearButton>
            <Button
              onClick={() => {
                setAnchorEl(null);
              }}
              variant="contained"
              color="success"
            >
              Done
            </Button>
          </Stack>
        </Stack>
      </StyledPopover>
      {!isDesktop && (
        <Stack
          sx={(theme) => ({
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2),
            borderBottom: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Text fontWeight="700">Property Type</Text>
          <RadioGroup onChange={handleChange} sx={{ margin: '0' }}>
            {propertyTypeOptions(currentPropertyType, currentSearchType).map(
              (propertyTypeOption, index: number) => (
                <FormControlLabel
                  className={propertyTypeOption.value}
                  value={propertyTypeOption.value}
                  key={index}
                  sx={{ margin: '0' }}
                  control={
                    <Radio
                      sx={(theme) => ({ padding: theme.spacing(0.5, 0) })}
                      checked={propertyTypeOption.defaultChecked}
                    />
                  }
                  label={propertyTypeOption.display}
                />
              )
            )}
          </RadioGroup>
        </Stack>
      )}
    </>
  );
}
